import React, { useEffect } from "react";

import PropTypes from "prop-types";

import Icon from "shared/components/Icon";
import { RegularText } from "shared/components/Typography";
import Well from "shared/components/Well";

import { useIncomeSurfaceGate } from "./DealSetupForm/IncomeSurfaceGateContext";
import { useIncomeSurfaceByPersona } from "./utils";

// Renders a gate banner when ANY persona has no active income surface
// (Argyle/Plaid/VERO GO). Doc configuration is still visible but dimmed
// with an explanation so managers understand which personas can't have
// docs configured. Matches the frontend guard in useGuardedIncomeToggle
// and the V1 expression gate on the backend — doc requirements for a
// persona without an income surface won't reach that applicant.
export const IncomeSurfaceGate = ({ children }) => {
  const perPersona = useIncomeSurfaceByPersona();
  const blockedPersonas = Object.entries(perPersona)
    .filter(([, state]) => state.blocked)
    .map(([key]) => key);
  const { reportBlockedPersonas } = useIncomeSurfaceGate();
  const gateIdRef = React.useRef(
    `income-surface-gate-${Math.random().toString(36).slice(2)}`
  );
  const blockedKey = blockedPersonas.join("|");

  useEffect(() => {
    reportBlockedPersonas(gateIdRef.current, blockedPersonas);
    return () => reportBlockedPersonas(gateIdRef.current, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockedKey, reportBlockedPersonas]);

  const personaLabel = (key) =>
    ({ applicant: "applicants", occupant: "occupants", guarantor: "guarantors" })[
      key
    ] || key;
  const personasList = blockedPersonas.map(personaLabel).join(", ");

  // Visually dim to reinforce the banner but leave the grid editable so
  // the manager can still turn off rows to clear the stranded state. The
  // only escape from "docs on, income off" is either (a) re-enabling an
  // income surface at Screening Setup or (b) deactivating the offending
  // rows here — so we must keep (b) accessible.
  return (
    <>
      {blockedPersonas.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Well lightWarning largePadding>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Icon.OrangeExclamationSmall />
              <RegularText>
                Documents for {personasList} won&apos;t render until Income or
                Balances Verification is enabled under Screening Setup.
              </RegularText>
            </div>
          </Well>
        </div>
      )}
      <div style={blockedPersonas.length > 0 ? { opacity: 0.6 } : undefined}>
        {children}
      </div>
    </>
  );
};

IncomeSurfaceGate.propTypes = {
  children: PropTypes.node.isRequired,
};

export default IncomeSurfaceGate;
